import Validation from './validation';

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Window helper service
 */
class WindowHelper {
  windowOpenExtended(url: string, w: number, h: number, target?: string, extendedOptions?: Record<string, unknown>): Window | null {
    const options: Record<string, unknown> = {
      width: w,
      height: h,
      scrollbars: 0,
      target: target ?? '_blank'
    };

    if (extendedOptions) {
      for (const key in extendedOptions) {
        if (Validation.hasProperty(extendedOptions, key)) {
          options[key] = extendedOptions[key];
        }
      }
    }

    // fixes dual-screen position for most browsers
    const screen = window.screen;
    const dualScreenLeft = window.screenLeft ?? 0;
    const dualScreenTop = window.screenTop ?? 0;
    let width;
    let height;

    if (window.innerWidth) {
      width = window.innerWidth;
    } else if (document.documentElement.clientWidth) {
      width = document.documentElement.clientWidth;
    } else {
      width = screen.width;
    }

    if (window.innerHeight) {
      height = window.innerHeight;
    } else if (document.documentElement.clientHeight) {
      height = document.documentElement.clientHeight;
    } else {
      height = screen.height;
    }

    options.left = ((width / 2) - (w / 2)) + dualScreenLeft;
    options.top = ((height / 2) - (h / 2)) + dualScreenTop;

    return this.openWindow(url, options);
  }

  /**
   * Opens a new window.
   * @param {string} url
   * @param {Object=} [optOptions]
   * @param {Window=} [optParentWin]
   * @return {Window} Returns the window object that was opened.
   */
  openWindow(url: string, optOptions: Record<string, unknown>, optParentWin?: Window) {
    optOptions = optOptions || {};

    const parentWin = optParentWin ?? window;

    let href = url;
    const target = optOptions.target as string;

    const sb = [];
    for (const option in optOptions) {
      switch (option) {
        case 'width':
        case 'height':
        case 'top':
        case 'left':
          sb.push(option + '=' + optOptions[option]?.toString());
          break;
        case 'target':
        case 'noreferrer':
          break;
        default:
          sb.push(option + '=' + (optOptions[option] ? 1 : 0));
      }
    }
    const optionString = sb.join(',');

    let newWin;
    if (optOptions.noreferrer) {
      // Use a meta-refresh to stop the referrer from being included in the request headers.
      newWin = parentWin.open('', target, optionString);
      if (newWin) {
        newWin.opener = null;
        href = escapeHtml(href);
        newWin.document.write('<META HTTP-EQUIV="refresh" content="0; url='
          + href + '">');
        newWin.document.close();
      }
    } else {
      newWin = parentWin.open(href, target, optionString);
    }
    // newWin is null if a popup blocker prevented the window open.
    return newWin;
  }
}

export default new WindowHelper();
